import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { ROLES, isAdminRoleName, isMegaAdminRoleName } from '@/core/security/roles';
import { tokenRequiresPasswordChange } from '@/shared/services/auth/passwordChange';
import type { LoginRequest, OtpVerifyRequest, User } from '../models';
import { isSupplierToken } from '../utils/supplierSession';

/**
 * Whose one-time code this is (2026-10-23): a user's (the staff sign-in) or a supplier's (the Supplier Bid Quotation
 * portal). Set when the code is requested; Resend and Verify follow it, so the shared OTP screens need not know.
 */
export type OtpAudience = 'user' | 'supplier';

interface OtpState {
  /** Carried across otp-send -> otp-verify so the screen can show it. */
  email: string;
  /** Which sign-in the code belongs to; 'user' unless the supplier portal asked for it. */
  audience: OtpAudience;
  sending: boolean;
  verifying: boolean;
  resending: boolean;
  otpSent: boolean;
  /** Absolute timestamps, computed in the saga -- reducers stay pure. */
  expiresAt: number | null;
  resendAvailableAt: number | null;
  /**
   * The code's full lifetime as the server reported it, kept so the verify
   * screen can say how long the code is good for without a second copy of the
   * number. Distinct from `expiresAt`, which is how much of it is left.
   */
  ttlSeconds: number | null;
  error: string | null;
  /** Bumped on every rejection so the form knows to clear and refocus. */
  failureCount: number;
}

export interface AuthState {
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  tokenType: string;
  /** Absolute epoch ms; derived in the saga so the reducer stays pure. */
  expiresAt: number | null;
  /**
   * False when the session is valid but GET /users/me failed. loginapi.md
   * section 8 requires the user still reach the dashboard, with limited
   * access, so this flags the degraded state rather than blocking entry.
   */
  profileLoaded: boolean;
  loading: boolean;
  error: string | null;
  /** False until the hydration saga has settled. Guards a flash-redirect. */
  hydrated: boolean;
  /**
   * Who signed OUT last, where the session was ended deliberately; null where it merely expired, where
   * there has been no session at all, or once somebody has signed back in.
   *
   * <p>It answers one question, at sign-in: **is the destination on the sign-in page mine?**
   * `ProtectedRoute` remembers where a bounced visitor was headed, and that memory sits on the sign-in
   * page waiting for whoever arrives next. Returned to the person who left it, it is exactly right — sign
   * back in and carry on where you were. Handed to somebody else, it sends them to a page that was never
   * theirs, and on a shared machine that is the ordinary case rather than the odd one.
   *
   * <p>An id rather than a flag, because "mine" is the whole of the rule. See `LoginPage`.
   */
  signedOutUserId: string | null;
  otp: OtpState;
}

const initialOtpState: OtpState = {
  email: '',
  audience: 'user',
  sending: false,
  verifying: false,
  resending: false,
  otpSent: false,
  expiresAt: null,
  resendAvailableAt: null,
  ttlSeconds: null,
  error: null,
  failureCount: 0,
};

const initialState: AuthState = {
  user: null,
  token: null,
  refreshToken: null,
  tokenType: 'Bearer',
  expiresAt: null,
  profileLoaded: false,
  loading: false,
  error: null,
  hydrated: false,
  signedOutUserId: null,
  otp: initialOtpState,
};

interface SessionTokens {
  token: string;
  refreshToken?: string;
  tokenType?: string;
  /** Absolute epoch ms, computed by the saga from the API's expiresIn. */
  expiresAt?: number;
}

interface OtpTimers {
  expiresAt: number;
  resendAvailableAt: number;
  ttlSeconds: number;
}

type SessionPayload = SessionTokens & { user: User; profileLoaded?: boolean };

function applySession(state: AuthState, payload: SessionPayload): void {
  state.user = payload.user;
  state.token = payload.token;
  state.refreshToken = payload.refreshToken ?? null;
  state.tokenType = payload.tokenType ?? 'Bearer';
  state.expiresAt = payload.expiresAt ?? null;
  state.profileLoaded = payload.profileLoaded ?? true;
  state.loading = false;
  state.error = null;
}

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    /* -- password ------------------------------------------------------- */
    loginStart: (state, _action: PayloadAction<LoginRequest>) => {
      state.loading = true;
      state.error = null;
      /*
        `signedOutUserId` is deliberately NOT cleared here. It is read after this, when the sign-in has
        resolved and there is somebody to compare against; clearing it now would make every arrival look
        like the person who left.
      */
    },
    loginSuccess: (state, action: PayloadAction<SessionPayload>) => {
      applySession(state, action.payload);
    },
    loginFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    /* -- session -------------------------------------------------------- */
    logoutStart: (state) => {
      state.loading = true;
    },
    /**
     * `hydrated` stays true: section 23.7 returns `initialState`, where it is
     * false, which leaves ProtectedRoute showing its spinner forever instead
     * of redirecting to /login.
     *
     * <p>`deliberate` separates signing out from a session simply ending. Both clear everything; only the
     * first records WHOSE session it was, which is what lets the sign-in page tell the person coming back
     * from the next person to use the machine.
     */
    logout: (state, action: PayloadAction<{ deliberate?: boolean } | undefined>) => ({
      ...initialState,
      hydrated: true,
      signedOutUserId: action.payload?.deliberate === true ? (state.user?.id ?? null) : null,
    }),
    /** Token refresh only -- must not touch `user`. */
    setTokens: (state, action: PayloadAction<SessionTokens>) => {
      state.token = action.payload.token;
      if (action.payload.refreshToken !== undefined) {
        state.refreshToken = action.payload.refreshToken;
      }
      if (action.payload.tokenType !== undefined) {
        state.tokenType = action.payload.tokenType;
      }
      if (action.payload.expiresAt !== undefined) {
        state.expiresAt = action.payload.expiresAt;
      }
    },
    setHydrated: (state) => {
      state.hydrated = true;
    },
    /** No-op trigger for the hydration saga. */
    hydrateAuth: (state) => state,
    setUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
      state.profileLoaded = true;
    },
    clearError: (state) => {
      state.error = null;
      state.otp.error = null;
    },

    /* -- OTP ------------------------------------------------------------ */
    sendOtpStart: (state, action: PayloadAction<{ email: string; audience?: OtpAudience }>) => {
      state.otp.sending = true;
      state.otp.error = null;
      state.otp.otpSent = false;
      state.otp.email = action.payload.email;
      state.otp.audience = action.payload.audience ?? 'user';
    },
    sendOtpSuccess: (state, action: PayloadAction<OtpTimers>) => {
      state.otp.sending = false;
      state.otp.otpSent = true;
      state.otp.expiresAt = action.payload.expiresAt;
      state.otp.resendAvailableAt = action.payload.resendAvailableAt;
      state.otp.ttlSeconds = action.payload.ttlSeconds;
      state.otp.failureCount = 0;
    },
    sendOtpFailure: (state, action: PayloadAction<string>) => {
      state.otp.sending = false;
      state.otp.error = action.payload;
    },

    verifyOtpStart: (state, _action: PayloadAction<OtpVerifyRequest & { audience?: OtpAudience }>) => {
      state.otp.verifying = true;
      state.otp.error = null;
    },
    verifyOtpSuccess: (state, action: PayloadAction<SessionPayload>) => {
      applySession(state, action.payload);
      state.otp = initialOtpState;
    },
    verifyOtpFailure: (state, action: PayloadAction<string>) => {
      state.otp.verifying = false;
      state.otp.error = action.payload;
      state.otp.failureCount += 1;
    },

    resendOtpStart: (state, _action: PayloadAction<{ email: string; audience?: OtpAudience }>) => {
      state.otp.resending = true;
      state.otp.error = null;
    },
    resendOtpSuccess: (state, action: PayloadAction<OtpTimers>) => {
      state.otp.resending = false;
      state.otp.expiresAt = action.payload.expiresAt;
      state.otp.resendAvailableAt = action.payload.resendAvailableAt;
      state.otp.ttlSeconds = action.payload.ttlSeconds;
      state.otp.failureCount = 0;
    },
    resendOtpFailure: (state, action: PayloadAction<string>) => {
      state.otp.resending = false;
      state.otp.error = action.payload;
    },

    /**
     * Step back to the email screen without losing the address.
     *
     * Needed because LoginPage advances to otp-verify whenever
     * `mode === 'otp-send' && otpSent`. Leaving otpSent true would make
     * "Change" bounce straight back to the verify screen.
     */
    clearOtpSent: (state) => {
      state.otp.otpSent = false;
      state.otp.error = null;
      state.otp.failureCount = 0;
    },

    clearOtpState: (state) => {
      state.otp = initialOtpState;
    },
  },
});

export const {
  loginStart,
  loginSuccess,
  loginFailure,
  logoutStart,
  logout,
  setTokens,
  setHydrated,
  hydrateAuth,
  setUser,
  clearError,
  sendOtpStart,
  sendOtpSuccess,
  sendOtpFailure,
  verifyOtpStart,
  verifyOtpSuccess,
  verifyOtpFailure,
  resendOtpStart,
  resendOtpSuccess,
  resendOtpFailure,
  clearOtpSent,
  clearOtpState,
} = authSlice.actions;

/**
 * Selectors take the slice shape structurally rather than importing RootState.
 * RootState is `ReturnType<typeof rootReducer>`, and rootReducer includes this
 * file -- annotating with it makes the type circular.
 */
export interface AuthRootState {
  auth: AuthState;
}

export const selectAuth = (state: AuthRootState) => state.auth;
export const selectUser = (state: AuthRootState) => state.auth.user;
export const selectToken = (state: AuthRootState) => state.auth.token;
export const selectRefreshToken = (state: AuthRootState) => state.auth.refreshToken;
/** Derived, per section 23.7 -- not a stored field. */
export const selectIsAuthenticated = (state: AuthRootState) =>
  Boolean(state.auth.token) && Boolean(state.auth.user);
/**
 * Reads the raw API role name, not the mapped slugs -- a Mega Admin maps to
 * 'admin' like any other admin, so `roles` cannot tell them apart.
 *
 * The shell branches on this to give Mega Admin a fixed Organization Setup
 * sidebar and to keep /sidebar/menu, /notifications/count and
 * /dashboard/summary from being requested at all for that role.
 */
export const selectIsMegaAdmin = (state: AuthRootState) =>
  isMegaAdminRoleName(state.auth.user?.roleName);

/**
 * A tenant administrator, read from the same raw API role name.
 *
 * Mutually exclusive with {@link selectIsMegaAdmin} -- both map to the 'admin'
 * slug, so `roles` cannot separate them. The shell branches on this to keep
 * Organization Setup off an Admin's sidebar and its APIs unrequested, and to
 * leave the dashboard's own calls unissued for a role that does not use them.
 */
export const selectIsAdmin = (state: AuthRootState) =>
  isAdminRoleName(state.auth.user?.roleName);
export const selectAuthLoading = (state: AuthRootState) => state.auth.loading;
export const selectTokenType = (state: AuthRootState) => state.auth.tokenType;
export const selectTokenExpiry = (state: AuthRootState) => state.auth.expiresAt;
/** False when the session is valid but the profile call failed. */
export const selectProfileLoaded = (state: AuthRootState) => state.auth.profileLoaded;
export const selectAuthError = (state: AuthRootState) => state.auth.error;
export const selectHydrated = (state: AuthRootState) => state.auth.hydrated;
/** Who signed out last, for deciding whether a remembered destination is theirs — see `AuthState`. */
export const selectSignedOutUserId = (state: AuthRootState) => state.auth.signedOutUserId;
export const selectOtp = (state: AuthRootState) => state.auth.otp;
/**
 * Whether the session is a supplier's, signed in to the Supplier Bid Quotation portal (2026-10-23) -- read from the
 * access token's own `user_type` claim, like `selectPasswordChangeRequired`.
 */
export const selectIsSupplierSession = (state: AuthRootState) => isSupplierToken(state.auth.token);
/**
 * Whether BIDs open as the Supplier Bid Quotation for this session (2026-10-27): a supplier signed in to the portal, or
 * a user whose role is Supplier. Mega Admin, Admin, Initiator and every other role keep the buyer's BIDs.
 */
export const selectIsSupplierRole = (state: AuthRootState) =>
  isSupplierToken(state.auth.token) || (state.auth.user?.roles ?? []).includes(ROLES.SUPPLIER);
/**
 * The session still holds its first (provisioned) password and must change it before anything else.
 * Derived from the access token's own claim -- see `tokenRequiresPasswordChange` -- so a refresh that
 * follows the change drops it without anything here having to be cleared.
 */
export const selectPasswordChangeRequired = (state: AuthRootState) =>
  tokenRequiresPasswordChange(state.auth.token);

export const authReducer = authSlice.reducer;
export default authSlice.reducer;
